import styles from "./ScaleSelector.module.scss";

import {
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

interface ScaleSelectorProps {
  options: { value: string; label: string }[];
  value: string | undefined;
  onSelect: (value: string) => void;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  minLabel?: string;
  maxLabel?: string;
}

export const ScaleSelector = ({
  options,
  value,
  onSelect,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  minLabel,
  maxLabel,
}: ScaleSelectorProps) => {
  const minLabelId = useId();
  const maxLabelId = useId();
  const optionValues = useMemo(
    () => options.map((option) => option.value),
    [options],
  );
  const optionRefs = useRef(new Map<string, HTMLButtonElement>());
  const [focusedOption, setFocusedOption] = useState<string>();
  const preferredTabStop = focusedOption ?? value;
  const tabStop =
    preferredTabStop !== undefined && optionValues.includes(preferredTabStop)
      ? preferredTabStop
      : optionValues[0];

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      // Treat a double-click as one selection; repeated clicks should not
      // restart the survey delay.
      if (event.detail > 1) return;

      onSelect(event.currentTarget.value);
    },
    [onSelect],
  );

  const handleFocus = useCallback((event: FocusEvent<HTMLButtonElement>) => {
    setFocusedOption(event.currentTarget.value);
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      const currentOption = event.currentTarget.value;
      const currentIndex = optionValues.indexOf(currentOption);
      let nextIndex: number | undefined;

      if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        nextIndex = (currentIndex + 1) % optionValues.length;
      } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        nextIndex =
          (currentIndex - 1 + optionValues.length) % optionValues.length;
      } else if (event.key === "Home") {
        nextIndex = 0;
      } else if (event.key === "End") {
        nextIndex = optionValues.length - 1;
      }

      if (nextIndex === undefined) return;

      event.preventDefault();
      const nextOption = optionValues[nextIndex];
      setFocusedOption(nextOption);
      optionRefs.current.get(nextOption)?.focus();
    },
    [optionValues],
  );

  const hasEndLabels = Boolean(minLabel || maxLabel);
  const describedBy = [
    ariaDescribedBy,
    minLabel ? minLabelId : undefined,
    maxLabel ? maxLabelId : undefined,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={styles.container}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={describedBy || undefined}
    >
      <div className={styles.options} data-option-count={options.length}>
        {options.map((option) => (
          <button
            key={option.value}
            ref={(element) => {
              if (element) {
                optionRefs.current.set(option.value, element);
              } else {
                optionRefs.current.delete(option.value);
              }
            }}
            type="button"
            value={option.value}
            className={styles.option}
            role="radio"
            aria-checked={option.value === value}
            aria-label={option.label}
            tabIndex={option.value === tabStop ? 0 : -1}
            onClick={handleClick}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
          >
            {option.label}
          </button>
        ))}
      </div>
      {hasEndLabels ? (
        <div className={styles.endLabels}>
          <span id={minLabelId}>{minLabel}</span>
          <span id={maxLabelId}>{maxLabel}</span>
        </div>
      ) : null}
    </div>
  );
};
